import React, { FC, useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  Chip,
  Divider,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  CircularProgress,
  TextField,
  Tooltip,
  Alert,
  AlertTitle,
} from '@mui/material';
import {
  Print as PrintIcon,
  Download as DownloadIcon,
  Close as CloseIcon,
  Email as EmailIcon,
  Payment as PaymentIcon,
} from '@mui/icons-material';
import { format, parseISO } from 'date-fns';
import { APPOINTMENT_PRICING } from '../constants/appointmentPricing';
import api from '../utils/api';
import { formatCents, formatDollars } from '../utils/money';
import { sendErrorMessage } from '../utils/sendErrorMessage';
import { formatCalendarDate, formatPaymentDay } from '../utils/dateUtils';
import { withOfflineFallback, readClinic, readInvoiceBalance, useIsOffline, skipWhenOffline } from '../offline';
import { calculateTax, LineItem as BillingLineItem } from './InvoiceLineItemsEditor';
import { useSnackbar } from 'notistack';
import { useQueryClient } from 'react-query';
import { useAnalytics } from '../hooks/useAnalytics';
import { useClinicTimezone, useFormatInstant } from '../hooks/useClinicTimezone';
import { useRBAC } from '../auth/useRBAC';
import { invoicingControls } from '../auth/pageControls';
import RefundPaymentDialog from './RefundPaymentDialog';
import IssueCreditDialog from './IssueCreditDialog';
import type { components } from '../types/api.generated';

// From the server's response class (fix 36), so a removed clinic field breaks the build.
type ClinicDto = components['schemas']['ClinicResponseDto'];
type ClinicInfo = Pick<ClinicDto, 'id' | 'name' | 'address' | 'phone' | 'email'> &
  Partial<Pick<ClinicDto, 'logoUrl' | 'taxMode'>>;

interface Owner {
  name: string;
  email?: string;
  phone?: string;
}

interface InvoiceDetailProps {
  open: boolean;
  onClose: () => void;
  onRecordPayment?: () => void;
  /** Called after the payer/split banner re-bills this invoice, so the parent can refresh + close. */
  onSplit?: () => void;
  invoice: {
    id: string;
    invoiceNumber: string;
    clientId?: string;
    splitGroupId?: string;
    appointments?: Array<{
      id: string;
      type: string;
      date?: string;
      location?: string;
    }>;
    horse: {
      // Selected by billing.service.findOne, which is what feeds this dialog.
      // Needed to resolve the horse's OWNER links for the payer/split banners.
      id?: string;
      name: string;
      owners: Owner[];
      ownerName?: string;
      ownerEmail?: string;
      ownerPhone?: string;
      ownerAddress?: string;
    };
    client?: {
      id: string;
      name: string;
      email?: string;
      phone?: string;
    };
    // One-time payer snapshot stored on the invoice (no client record)
    thirdPartyPayer?: {
      name: string;
      email?: string;
      phone?: string;
      address?: string;
    };
    amount: number;
    status: 'pending' | 'paid' | 'partially_paid' | 'overdue' | 'cancelled';
    description?: string;
    internalNote?: string;
    clientNote?: string | null;
    issuedAt: string;
    dueDate: string;
    serviceDate?: string;
    taxRate?: number;
    taxAmount?: number;
    paidAt?: string;
    finalizedAt?: string;
    finalizedBy?: string;
    lineItems?: Array<{
      description: string;
      quantity: number;
      unitPrice: number;
      total: number;
      // Rx auto-bill's trace back to the dispense event that created this line
      // (medications.service). Its PRESENCE is the honest marker that this
      // invoice was minted by an Rx dispense — the description string is not
      // (only the brand-new-invoice branch sets 'Rx dispenses'; appends to an
      // existing invoice would be missed). FE type only; no API change.
      rxDispenseEventId?: string;
      // BILL-DISC: per-line discount (total is post-discount)
      discountType?: 'percentage' | 'fixed';
      discountValue?: number;
      discountAmount?: number;
      discountReason?: string;
      // Hospital auto-bill: the clinic-local calendar day this charge belongs
      // to, stored once by the backend as a plain 'YYYY-MM-DD' string so no
      // renderer ever re-derives it from a timestamp. `administeredAt` is the
      // instant the dose was given — intra-day ordering only.
      serviceDate?: string;
      administeredAt?: string;
    }>;
    // BILL-DISC: invoice-level discount (amount is stored post-discount)
    discountType?: string | null;
    discountValue?: number | null;
    discountAmount?: number | null;
    discountReason?: string | null;
    // BILL-E: advisory void eligibility from findOne — voidInvoice re-checks
    // under a row lock. 'co_owner_paid' means a sibling in the split group has
    // payments, so the correction path is an account credit, not a void.
    voidEligible?: boolean;
    voidBlockedReason?: 'has_payments' | 'co_owner_paid' | null;
    additionalServices?: string[];
    splitGroupTotal?: number;
    splitPercentage?: number;
    splitInvoices?: Array<{
      id: string;
      invoiceNumber: string;
      amount: number;
      taxAmount?: number;
      status: string;
      percentage?: number;
      groupTotal?: number;
      client?: { id: string; name: string };
    }>;
  };
}

interface PaymentRecord {
  id: string;
  amount: number;
  status?: string;
  paymentType: string;
  paymentMethod: string;
  paymentDate: string;
  referenceNote: string | null;
  cardBrand: string | null;
  cardLast4: string | null;
  voidReason?: string | null;
  payerName?: string | null;
  stripePaymentIntentId?: string | null;
  stripeRefundId?: string | null;
  refundFailedAt?: string | null;
  refundReason?: string | null;
  // Fix 17, display only: the invoice still counts these payments in full.
  partiallyRefundedCents?: number | null;
  disputeLost?: boolean;
  disputedAmountCents?: number | null;
}

interface BalanceData {
  total: number;
  taxAmount: number;
  totalWithTax: number;
  totalPaid: number;
  processingAmount?: number;
  balanceDue: number;
  payments: PaymentRecord[];
  // Voided history rows live in a separate additive field so the mobile app's
  // `payments` contract (succeeded/processing only) stays untouched.
  voidedPayments?: PaymentRecord[];
  refundedPayments?: PaymentRecord[];
  disputedPayments?: PaymentRecord[];
}

const PAYMENT_TYPE_LABELS: Record<string, string> = {
  cash: 'Cash',
  check: 'Check',
  credit_card: 'Credit Card',
  debit_card: 'Debit Card',
  other: 'Other',
  card: 'Credit Card',
  ach: 'Bank Transfer',
  credit: 'Account Credit', // BILL-CRED: applied client credit
};

// Fix 17: why a disputed payment no longer counts. The amount is the card charge's, which can
// be part of this payment or, for a statement charge, span several invoices.
const disputeNote = (payment: PaymentRecord): string => {
  const amount = payment.disputedAmountCents ? ` ${formatCents(payment.disputedAmountCents)} of the card charge` : ' this card charge';
  return payment.disputeLost
    ? `Dispute lost. The cardholder's bank took back${amount}, so this payment no longer counts.`
    : `The cardholder disputed${amount}. This payment does not count while the dispute is open.`;
};

type InvoiceLineItem = NonNullable<InvoiceDetailProps['invoice']['lineItems']>[number];

interface LineItemDayGroup {
  key: string;
  label: string;
  subtotalLabel: string;
  items: InvoiceLineItem[];
  subtotal: number;
}

/** Charges with no `serviceDate` (manual additions on a hospital invoice) land here, last. */
const OTHER_CHARGES_KEY = '__other__';

const roundDollars = (value: number) => Math.round(value * 100) / 100;

const sumLineTotals = (rows: InvoiceLineItem[]) =>
  roundDollars(rows.reduce((sum, row) => sum + Number(row.total || 0), 0));

/** Undosed/legacy rows sink to the bottom of their day rather than jumping to the top. */
const byAdministeredAt = (a: InvoiceLineItem, b: InvoiceLineItem) => {
  const at = a.administeredAt ? Date.parse(a.administeredAt) : NaN;
  const bt = b.administeredAt ? Date.parse(b.administeredAt) : NaN;
  if (Number.isNaN(at) && Number.isNaN(bt)) return 0;
  if (Number.isNaN(at)) return 1;
  if (Number.isNaN(bt)) return -1;
  return at - bt;
};

/**
 * A hospital stay bills onto ONE invoice, so its lines read as day-by-day
 * sections — that is how a clinic reads the stay down the phone to an owner.
 * Returns null when no line carries a `serviceDate`, which keeps every ordinary
 * invoice on the flat table below.
 */
const groupLineItemsByServiceDate = (items: InvoiceLineItem[]): LineItemDayGroup[] | null => {
  if (!items.some((item) => typeof item.serviceDate === 'string' && item.serviceDate)) return null;

  const byDay = new Map<string, InvoiceLineItem[]>();
  const undated: InvoiceLineItem[] = [];
  items.forEach((item) => {
    const day = typeof item.serviceDate === 'string' ? item.serviceDate.slice(0, 10) : '';
    if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) {
      undated.push(item);
      return;
    }
    const bucket = byDay.get(day);
    if (bucket) bucket.push(item);
    else byDay.set(day, [item]);
  });

  const groups: LineItemDayGroup[] = Array.from(byDay.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([day, rows]) => ({
      key: day,
      // parseISO on the date-only string yields LOCAL midnight. `new Date(day)`
      // would be UTC midnight and print the previous day in every negative-offset
      // zone — the day-rollback bug class this repo already carries.
      label: format(parseISO(day), 'EEEE, MMMM d, yyyy'),
      subtotalLabel: 'Day subtotal',
      items: [...rows].sort(byAdministeredAt),
      subtotal: sumLineTotals(rows),
    }));

  if (undated.length > 0) {
    groups.push({
      key: OTHER_CHARGES_KEY,
      label: 'Other charges',
      subtotalLabel: 'Other charges subtotal',
      items: undated,
      subtotal: sumLineTotals(undated),
    });
  }

  return groups;
};

/** One markup definition, shared by the flat and grouped tables. */
const renderLineItemRow = (item: InvoiceLineItem, key: React.Key) => {
  // BILL-DISC: struck-through original price + reason tag
  const hasDiscount = (item.discountAmount || 0) > 0;
  const originalTotal = hasDiscount
    ? Math.round((item.total + (item.discountAmount || 0)) * 100) / 100
    : item.total;
  return (
    <TableRow key={key}>
      <TableCell>
        {item.description}
        {hasDiscount && item.discountReason && (
          <Chip
            label={item.discountReason}
            size="small"
            variant="outlined"
            color="primary"
            sx={{ ml: 1, height: 18, fontSize: '0.65rem' }}
          />
        )}
      </TableCell>
      <TableCell align="center">{item.quantity}</TableCell>
      <TableCell align="right">${item.unitPrice.toFixed(2)}</TableCell>
      <TableCell align="right">
        {hasDiscount && (
          <Typography
            component="span"
            variant="caption"
            sx={{ textDecoration: 'line-through', color: 'text.disabled', mr: 0.75 }}
          >
            ${originalTotal.toFixed(2)}
          </Typography>
        )}
        ${item.total.toFixed(2)}
      </TableCell>
    </TableRow>
  );
};

const InvoiceDetail: FC<InvoiceDetailProps> = ({ open, onClose, onRecordPayment, onSplit, invoice }) => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const { trackFeatureUsed } = useAnalytics();
  // The INSTANTS on this dialog (issue date, paid date, the appointment-date fallback)
  // render in the CLINIC's zone, never the viewer's browser zone — per-site comments
  // below. The calendar dates (due, service) never go through this: they parse the
  // date part via formatCalendarDate. Kinds per Docs/architecture/DATE-KINDS.md §1.
  const formatInstant = useFormatInstant();
  // The raw zone, for the ONE field that needs a per-row decision rather than a
  // bound instant formatter (Payment.paymentDate — see the payments table below).
  const clinicTz = useClinicTimezone();
  // Email/PDF/Print all need the server (Postmark send / server-rendered PDF);
  // the invoice view itself renders offline, so only the actions are disabled.
  const isOffline = useIsOffline();
  const [clinicInfo, setClinicInfo] = useState<ClinicInfo | null>(null);
  const [loadingClinic, setLoadingClinic] = useState(true);
  const [generatingPDF, setGeneratingPDF] = useState(false);
  const [preparingPrint, setPreparingPrint] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [balanceData, setBalanceData] = useState<BalanceData | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  // method: carried so a voided credit-application can refresh the client's
  // Available Credit (the void writes a reversal entry that restores balance).
  const [voidTarget, setVoidTarget] = useState<{ kind: 'payment' | 'invoice'; id: string; label: string; method?: string } | null>(null);
  const [refundTarget, setRefundTarget] = useState<PaymentRecord | null>(null);
  // BILL-E: once money has moved (here or anywhere in the split group), void
  // is off the table and the correction path is an account credit instead.
  const [issueCreditOpen, setIssueCreditOpen] = useState(false);
  // invoice.voidEligible/voidBlockedReason are a findOne snapshot from when the
  // parent fetched the invoice — stale the moment a payment is voided in this
  // dialog (the invoice Void button wouldn't appear until close/reopen). After
  // a payment void we refetch just these two fields; null = trust the props.
  const [freshVoidState, setFreshVoidState] = useState<{
    voidEligible?: boolean;
    voidBlockedReason?: 'has_payments' | 'co_owner_paid' | null;
  } | null>(null);
  // The dialog stays mounted across close/reopen — a slow eligibility refetch
  // for invoice A must never land on invoice B (guarded in the .then below).
  const invoiceIdRef = useRef(invoice.id);

  useEffect(() => {
    invoiceIdRef.current = invoice.id;
    setFreshVoidState(null);
  }, [invoice.id, open]);

  useEffect(() => {
    const fetchClinicInfo = async () => {
      try {
        // Offline: fall back to the synced clinic row so an offline-printed
        // invoice shows the real letterhead, not the placeholder. Throw if the
        // clinic isn't in the replica so the catch still applies the placeholder.
        const data = await withOfflineFallback(
          () => api.get('/clinics/current').then((r) => r.data),
          () => readClinic().then((c) => { if (!c) throw new Error('no offline clinic'); return c; }),
        )();
        setClinicInfo(data);
      } catch (error) {
        console.error('Failed to fetch clinic info:', error);
        setClinicInfo({
          id: '',
          name: 'Veterinary Clinic',
          address: 'Address not available',
          phone: 'Phone not available',
          email: 'Email not available',
        });
      } finally {
        setLoadingClinic(false);
      }
    };

    const fetchBalance = async () => {
      try {
        // Offline: compute the balance from the replica (same wiring as
        // EnhancedPaymentDialog) so a cash payment recorded offline still
        // shows in PAYMENTS RECEIVED and the balance chips.
        const data = await withOfflineFallback(
          () => api.get(`/payments/invoice/${invoice.id}/balance`).then((r) => r.data),
          () => readInvoiceBalance(invoice.id),
        )();
        setBalanceData(data);
      } catch (error) {
        console.error('Failed to fetch payment balance:', error);
      }
    };

    if (open) {
      fetchClinicInfo();
      fetchBalance();
    }

    return () => {
      setBalanceData(null);
    };
  }, [open, invoice.id, refreshKey]);


  // ── Payer + co-owner banners (INV-57 / ADR-0033) ───────────────────────────
  //
  // Rx auto-bill deliberately bills ONE payer for the whole dispense line
  // (highest share wins, earliest link breaks ties) — it does not split. That
  // is the right call at dispense time (nobody is standing at the invoice yet)
  // and the wrong end state for a co-owned horse, so the app ASKS to split when
  // the invoice is opened, using the existing `billToOwners` re-split rather
  // than a second split path.
  //
  // The payer-less banner is the other half: auto-invoices minted before the
  // server-side fix could carry NO clientId at all, which makes them invisible
  // to the statement run and to every client balance. That one is an error, not
  // a suggestion, so it is never dismissible.
  //
  // Both banners drive ONE action, `PUT /billing/:id { billToOwners: true }`,
  // which the controller gates on `manage:charges` (the P3 sweep split
  // `manage:billing`). The BUTTON is hidden
  // without that permission — never the informational text, because a
  // read-only viewer still needs to know an invoice has no payer so they can
  // tell someone who can fix it. This is a UI gate, not the enforcement
  // point: the backend guard is (frontend rules), and a refusal would still
  // surface through this dialog's `sendErrorMessage` handler.
  //
  // ⚠ TWO SLUGS ON THIS SCREEN, and `can.refund` is the one this component was missing. Writing
  // the invoice is `manage:charges`; taking money BACK is `manage:refunds` — `payments.controller`
  // `refund`/`void` and `billing.controller` `voidInvoice` — and a front desk that may edit an
  // invoice is not thereby allowed to reverse a payment on it. Both come from the screen's map
  // rather than an inline slug, which also replaces the old hand-rolled `!rbacLoading` guard: the
  // map holds every control open until THIS instance's answer is in (INV-107 trap (a)) and shows
  // them all if it never arrives, where `!rbacLoading` covered only the first of those windows.
  const rbac = useRBAC();
  const can = invoicingControls(rbac);
  const canManageBilling = can.edit;

  const [ownerLinks, setOwnerLinks] = useState<Array<{ clientId: string; name: string; ownershipPercentage?: number | null }> | null>(null);
  const [splitting, setSplitting] = useState(false);
  const [splitDismissed, setSplitDismissed] = useState(false);

  const isPayerless = !invoice.clientId && !invoice.thirdPartyPayer;
  // Openness mirrors the SERVER's own gates so the button cannot 400:
  // resplitAcrossOwners rejects paid/cancelled/partially_paid and re-checks
  // splitGroupId/finalizedAt under the row lock; update() rejects any edit on a
  // finalized invoice.
  const isOpenForRebill =
    !['paid', 'cancelled', 'partially_paid'].includes(invoice.status) && !invoice.finalizedAt;
  const noMoneyMoved =
    !balanceData || ((balanceData.totalPaid ?? 0) + (balanceData.processingAmount ?? 0)) <= 0;
  // The honest "auto-created by Rx dispense" marker: the tag auto-bill writes on
  // its line. NOT the description string — only the new-invoice branch sets
  // 'Rx dispenses', so appends to an existing invoice would be missed.
  const isRxAutoBilled = (invoice.lineItems ?? []).some(
    (li) => typeof li.rxDispenseEventId === 'string',
  );

  const dismissKey = `st.splitPrompt.dismissed.${invoice.id}`;
  useEffect(() => {
    // localStorage throws outright in some contexts (private mode, blocked
    // site data), so every read is guarded and a failure just shows the banner.
    try {
      setSplitDismissed(window.localStorage.getItem(dismissKey) === '1');
    } catch {
      setSplitDismissed(false);
    }
  }, [dismissKey]);

  const billableOwnerLinks = (ownerLinks ?? []).filter((o) => o.ownershipPercentage !== 0);
  // INV-57 all-zero anomaly: bill everyone, never nobody — same fallback the
  // server's resolveBillableOwners applies.
  const billableOwners = billableOwnerLinks.length ? billableOwnerLinks : (ownerLinks ?? []);

  const wantsSplitPrompt =
    !invoice.splitGroupId && isOpenForRebill && noMoneyMoved && isRxAutoBilled && !splitDismissed;
  const needsOwners = open && !!invoice.horse?.id && (isPayerless || wantsSplitPrompt);

  useEffect(() => {
    // Lazy, and only once the cheap conditions pass — the common single-owner,
    // properly-payered invoice costs no request at all.
    if (!needsOwners) {
      setOwnerLinks(null);
      return;
    }
    let cancelled = false;
    api
      .get(`/horses/${invoice.horse.id}/owners`)
      .then((r) => {
        if (!cancelled) setOwnerLinks(Array.isArray(r.data) ? r.data : []);
      })
      .catch(() => {
        if (!cancelled) setOwnerLinks([]);
      });
    return () => {
      cancelled = true;
    };
  }, [needsOwners, invoice.horse?.id, refreshKey]);

  const dismissSplitPrompt = () => {
    setSplitDismissed(true);
    try {
      window.localStorage.setItem(dismissKey, '1');
    } catch {
      /* per-device convenience only — losing it just shows the banner again */
    }
  };

  // ONE action covers both banners by design: with exactly one billable link
  // the server does a plain payer switch (and kills any stale payment link);
  // with two or more it re-splits, which is also the answer to the co-owner
  // question. Same route, same DTO field, no second split path.
  const rebillToOwners = async () => {
    if (!skipWhenOffline()) {
      enqueueSnackbar(
        'Splitting an invoice between owners needs a connection — try again when back online.',
        { variant: 'warning' },
      );
      return;
    }
    setSplitting(true);
    try {
      const result = await api.put(`/billing/${invoice.id}`, { billToOwners: true });
      const data = result.data;
      if (Array.isArray(data)) {
        enqueueSnackbar(
          `Invoice split into ${data.length} invoices — collect payments from each individually`,
          { variant: 'success' },
        );
      } else {
        enqueueSnackbar(
          `Re-billed in full to ${data?.client?.name || 'the owner on file'} — this horse has one linked owner`,
          { variant: 'info' },
        );
      }
      queryClient.invalidateQueries(['invoices']);
      queryClient.invalidateQueries(['billing-stats']);
      onSplit?.();
      onClose();
    } catch (error) {
      const feedback = sendErrorMessage(
        error,
        'Could not re-bill this invoice to the owners. Please try again.',
      );
      enqueueSnackbar(feedback.message, { variant: feedback.variant });
    } finally {
      setSplitting(false);
    }
  };

  const ownerNames = billableOwners.map((o) => o.name).filter(Boolean).join(', ');

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid':
        return 'success';
      case 'partially_paid':
        return 'info';
      case 'pending':
        return 'warning';
      case 'overdue':
        return 'error';
      case 'cancelled':
        return 'default';
      default:
        return 'default';
    }
  };

  const handlePrint = async () => {
    setPreparingPrint(true);
    try {
      const response = await api.get(`/billing/${invoice.id}/pdf`, {
        responseType: 'blob',
      });
      const pdfBlob = new Blob([response.data], { type: 'application/pdf' });
      const pdfUrl = URL.createObjectURL(pdfBlob);

      const printWindow = window.open(pdfUrl, '_blank');
      if (printWindow) {
        printWindow.onload = () => {
          setTimeout(() => {
            printWindow.print();
            setTimeout(() => URL.revokeObjectURL(pdfUrl), 1000);
          }, 500);
        };
      }
    } catch (error) {
      console.error('Error generating PDF for printing:', error);
    } finally {
      setPreparingPrint(false);
    }
  };

  const handleDownloadPDF = async () => {
    setGeneratingPDF(true);
    try {
      const response = await api.get(`/billing/${invoice.id}/pdf`, {
        responseType: 'blob',
      });
      const pdfBlob = new Blob([response.data], { type: 'application/pdf' });
      const pdfUrl = URL.createObjectURL(pdfBlob);

      const link = document.createElement('a');
      link.href = pdfUrl;
      link.download = `${invoice.status === 'paid' ? 'Receipt' : 'Invoice'}_${invoice.horse.name.replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(pdfUrl);
    } catch (error) {
      console.error('Error generating PDF:', error);
    } finally {
      setGeneratingPDF(false);
    }
  };

  const handleEmailInvoice = async () => {
    // RULING B-1, the same terminal rule as SendPaymentLinkDialog.resolveEmail: once a
    // third-party payer is present the owner legs are NOT consulted. `POST
    // /billing/:id/email` mails the recipient this line resolves and has no payer
    // fallback of its own (it 400s on a missing `recipientEmail`), so a chain that fell
    // through to `owners[0].email` emailed the insurer's invoice — amount, line items
    // and PDF — to the horse's owner. An email-less payer is a normal row
    // (`normalizeThirdPartyPayer` requires only a name), and the warning below is the
    // supported outcome: add the payer's email on the invoice first.
    const ownerEmail = invoice.client?.email
      || (invoice.thirdPartyPayer
        ? invoice.thirdPartyPayer.email
        : invoice.horse.ownerEmail
          || (Array.isArray(invoice.horse.owners) && invoice.horse.owners.length > 0 ? invoice.horse.owners[0]?.email : undefined));

    if (!ownerEmail) {
      enqueueSnackbar('No email address found for the invoice recipient. Please add an email address first.', { variant: 'warning' });
      return;
    }

    setSendingEmail(true);
    setEmailSent(false);
    try {
      await api.post(`/billing/${invoice.id}/email`, {
        recipientEmail: ownerEmail,
      });
      setEmailSent(true);
      setTimeout(() => setEmailSent(false), 3000);
      trackFeatureUsed('invoice_emailed');
      enqueueSnackbar(`${invoice.status === 'paid' ? 'Receipt' : 'Invoice'} email sent successfully!`, { variant: 'success' });
    } catch (error) {
      console.error('Failed to send invoice email:', error);
      // The old branch hardcoded "Failed to send invoice email." and threw away
      // error.response.data.message. On a 30s axios timeout the send may well
      // have SUCCEEDED — telling the user it failed makes them click again and
      // the client gets two receipts. See utils/sendErrorMessage.ts.
      const feedback = sendErrorMessage(error, 'Could not send the invoice email. Please try again.');
      enqueueSnackbar(feedback.message, { variant: feedback.variant });
    } finally {
      setSendingEmail(false);
    }
  };

  // Generate line items from invoice data
  const generateLineItems = (): InvoiceLineItem[] => {
    // First check if invoice already has saved line items
    if (invoice.lineItems && invoice.lineItems.length > 0) {
      return invoice.lineItems;
    }

    // Otherwise, generate from appointment data (legacy fallback for old invoices)
    const items = [];

    const appointment = invoice.appointments?.[0];
    if (appointment) {
      const appointmentType = appointment.type;
      const pricing = APPOINTMENT_PRICING[appointmentType];
      
      if (pricing) {
        items.push({
          description: `${appointmentType} - ${pricing.description}`,
          quantity: 1,
          unitPrice: pricing.basePrice,
          total: pricing.basePrice,
        });
      }
    } else if (invoice.description) {
      // If no appointment, use description and amount
      items.push({
        description: invoice.description,
        quantity: 1,
        unitPrice: invoice.amount,
        total: invoice.amount,
      });
    }

    return items;
  };

  const lineItems = generateLineItems();
  // null on every non-hospital invoice → the flat table renders unchanged.
  const dayGroups = groupLineItemsByServiceDate(lineItems);
  const subtotal = lineItems.reduce((sum, item) => sum + item.total, 0);
  const taxRate = invoice.taxRate || 0;
  const taxAmount = invoice.taxAmount || 0;
  const total = subtotal + taxAmount;
  // What the PDF and email print under Notes (ADR-0044): the description, then the client
  // note when it says something else.
  const notes = Array.from(
    new Set([invoice.description, invoice.clientNote].map((text) => text?.trim() ?? '').filter(Boolean)),
  ).join('\n\n');

  if (loadingClinic) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <Box display="flex" justifyContent="center" alignItems="center" p={4}>
          <CircularProgress />
        </Box>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <Box display="flex" justifyContent="space-between" alignItems="center" p={2}>
        <Typography variant="h5">Invoice Details</Typography>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>
      
      <DialogContent>
        {/*
          PAYER banner. A live invoice with neither a clientId nor a
          thirdPartyPayer has no assigned payer — a money defect, not a
          suggestion, so it is NOT dismissible. It clears itself the moment a
          payer is set. Rendered above the split banner and suppressing it:
          `billToOwners` on a 2+-owner horse performs the split too, so one
          action resolves both.

          ⚠ THE CONSEQUENCE IS PER-ARM — only the ZERO-owner arm is genuinely
          invisible to money, so only it says so. `clientId IS NULL` is NOT the
          same as "reaches no statement and no balance": both money surfaces
          fall back to the horse's OWNER HorseClient for unassigned invoices.
          `billing.service.classifyClientObligations`'s `fullyOwes` returns TRUE
          for a payer-less invoice on a SOLELY owned horse (and
          `monthly-statement-run.findCandidateClients` pulls those owners in on
          purpose), so it does reach that owner's statement; co-owned it is
          `needsSplit` — off statements until split, but still attributed to an
          owner by `reports.getClientBalances`. Claiming "it will not appear
          anywhere" in the arm that NAMES those owners contradicts both itself
          and the server, so each arm states its own truth.
        */}
        {isPayerless && (
          <Alert severity="error" sx={{ mb: 2 }} className="no-print">
            <AlertTitle>This invoice has no payer</AlertTitle>
            No client or third-party payer is assigned to it.
            {ownerLinks === null ? null : billableOwners.length > 0 ? (
              <Box mt={1}>
                <Typography variant="body2" gutterBottom>
                  It is attributed to the owner only by fallback, and while it is
                  co-owned it cannot go on a statement until it is split.
                </Typography>
                {/*
                  Same openness gate the split banner uses, for the same reason:
                  PUT /billing/:id fast-rejects a finalized invoice and one with
                  succeeded/processing payments, so on a paid or voided
                  payer-less invoice this button is a guaranteed 400. The TEXT
                  above still renders — a closed invoice with no payer is still
                  worth knowing about — only the dead action is replaced.
                */}
                {canManageBilling && isOpenForRebill && noMoneyMoved ? (
                  <Button
                    size="small"
                    variant="contained"
                    color="error"
                    disabled={splitting}
                    onClick={rebillToOwners}
                  >
                    {splitting ? 'Working…' : `Bill to ${ownerNames}`}
                  </Button>
                ) : canManageBilling ? (
                  <Typography variant="body2">
                    {ownerNames} own this patient, but a finalized, paid or voided
                    invoice can no longer be re-billed — issue a credit or a
                    replacement invoice in Billing to put it on an owner.
                  </Typography>
                ) : (
                  // No `manage:charges` — the fix is still worth NAMING, because
                  // this viewer is the one looking at the broken invoice.
                  <Typography variant="body2">
                    {ownerNames} own this patient. Ask someone with billing
                    permission to bill this invoice to them.
                  </Typography>
                )}
              </Box>
            ) : (
              <Box mt={1}>
                <Typography variant="body2">
                  It will not appear on any client statement or account balance: no
                  owners are linked to this patient either. Link an owner on the
                  patient&apos;s chart, or use Edit → Bill To to name a one-time payer.
                </Typography>
              </Box>
            )}
          </Alert>
        )}

        {/*
          CO-OWNER split prompt (INV-57 / manager rule R2). Rx auto-bill bills
          ONE payer for the whole dispense — correct at dispense time, wrong as
          an end state for a co-owned horse. Rather than suppressing the
          auto-bill (which would replace a split with a single-payer charge, or
          worse, re-offer the fill and charge it twice), the invoice stands and
          the app asks. Dismissal is per-invoice, per-device localStorage: once
          the invoice is actually split, `splitGroupId` makes this permanently
          false for every viewer, which is the real dismissal.
        */}
        {!isPayerless && wantsSplitPrompt && billableOwners.length > 1 && (
          <Alert
            severity="info"
            sx={{ mb: 2 }}
            className="no-print"
            onClose={dismissSplitPrompt}
          >
            <AlertTitle>This patient has more than one owner</AlertTitle>
            The Rx dispense on this invoice was billed in full to{' '}
            {invoice.client?.name || 'one owner'}. {ownerNames} share this patient — split it
            so each owner is billed their share.
            {canManageBilling && (
              <Box mt={1}>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={splitting}
                  onClick={rebillToOwners}
                >
                  {splitting ? 'Splitting…' : 'Split between owners'}
                </Button>
              </Box>
            )}
          </Alert>
        )}

        <div>
          <Box className="invoice-container" sx={{ 
            p: 3, 
            backgroundColor: 'white',
            fontFamily: 'Arial, Helvetica, sans-serif',
            WebkitFontSmoothing: 'antialiased',
            MozOsxFontSmoothing: 'grayscale',
            letterSpacing: '0.01em',
          }}>
            {/* Header */}
            <Grid container spacing={2} className="header">
              <Grid item xs={6}>
                {clinicInfo?.logoUrl && (
                  <Box
                    component="img"
                    src={clinicInfo.logoUrl}
                    alt={clinicInfo.name}
                    sx={{ maxHeight: 80, maxWidth: 200, objectFit: 'contain', display: 'block', mb: 1 }}
                  />
                )}
                <Typography variant="h4" className="invoice-title" gutterBottom>
                  INVOICE
                </Typography>
                <Box className="clinic-info">
                  <Typography variant="h6" fontWeight="bold">
                    {clinicInfo?.name || 'Veterinary Clinic'}
                  </Typography>
                  {clinicInfo?.address && <Typography variant="body2">{clinicInfo.address}</Typography>}
                  {clinicInfo?.phone && <Typography variant="body2">Phone: {clinicInfo.phone}</Typography>}
                  <Typography variant="body2">Email: {clinicInfo?.email || 'Email not available'}</Typography>
                </Box>
              </Grid>
              <Grid item xs={6} textAlign="right">
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Invoice #{invoice.invoiceNumber}
                </Typography>
                <Typography variant="body2">
                  {/* INSTANT (DATE-KINDS §1.4): Invoice.issuedAt is `@default(now())` and is never
                      written from a client-supplied date string (the create path does not set
                      it; billing.service.ts only preserves it on update). It is the MOMENT the
                      invoice was raised, so an invoice created 9 PM Eastern must read as that
                      Eastern day — the CLINIC's day. Not the browser's zone (wrong for remote
                      and relief staff, and this was browser-zoned until 2026-09 only because no
                      clinic-zone helper existed), and not formatCalendarDate, which would print
                      the UTC day and roll it FORWARD. */}
                  Issue Date:{' '}
                  <Box component="span" data-testid="invoice-issue-date">
                    {formatInstant(invoice.issuedAt, 'MMMM dd, yyyy')}
                  </Box>
                </Typography>
                {invoice.serviceDate && (
                  <Typography variant="body2">
                    Service Date: {formatCalendarDate(invoice.serviceDate)}
                  </Typography>
                )}
                <Typography variant="body2">
                  {/* The value carries the testid, not the row: e2e/specs/timezone-rendering.spec.ts
                      asserts the rendered CALENDAR DATE, and a locator that swallowed the
                      "Due Date:" label would be asserting on copy as well as on the value. */}
                  Due Date:{' '}
                  <Box component="span" data-testid="invoice-due-date">
                    {formatCalendarDate(invoice.dueDate)}
                  </Box>
                </Typography>
                {invoice.paidAt && (
                  <Typography variant="body2" color="success.main">
                    {/* INSTANT (DATE-KINDS §1.3 Invoice.paidAt): every writer stamps `new Date()`
                        at the moment the balance cleared (billing.service.ts, payments.service.ts).
                        B6 removed the one client-supplied path altogether — `UpdateInvoiceStatusDto`
                        now declares `status` only and PUT /billing/:id/status no longer converts a
                        `paidAt` from the body — so the field is server-stamped on EVERY path, not
                        merely on every path someone calls. Same reasoning as Issue Date above: the
                        clinic's zone, never the date part. */}
                    Paid:{' '}
                    <Box component="span" data-testid="invoice-paid-date">
                      {formatInstant(invoice.paidAt, 'MMMM dd, yyyy')}
                    </Box>
                  </Typography>
                )}
                <Box sx={{ mt: 1 }}>
                  <Chip
                    label={invoice.status === 'partially_paid' ? 'Partially Paid' : invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
                    color={getStatusColor(invoice.status) as any}
                    size="small"
                  />
                </Box>
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            {/* Bill To Section */}
            <Grid container spacing={2} className="bill-to">
              <Grid item xs={6}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  BILL TO:
                </Typography>
                {invoice.thirdPartyPayer && !invoice.client ? (
                  // One-time third-party payer snapshot stored on the invoice
                  <>
                    <Typography variant="body1" fontWeight="medium">
                      {invoice.thirdPartyPayer.name}
                    </Typography>
                    {invoice.thirdPartyPayer.email && (
                      <Typography variant="body2">{invoice.thirdPartyPayer.email}</Typography>
                    )}
                    {invoice.thirdPartyPayer.phone && (
                      <Typography variant="body2">{invoice.thirdPartyPayer.phone}</Typography>
                    )}
                    {invoice.thirdPartyPayer.address && (
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                        {invoice.thirdPartyPayer.address}
                      </Typography>
                    )}
                  </>
                ) : (
                  <>
                    <Typography variant="body1" fontWeight="medium">
                      {invoice.client?.name || invoice.horse.ownerName || (() => {
                        const owners = Array.isArray(invoice.horse.owners) ? invoice.horse.owners : [];
                        return owners.length > 0 && owners[0]?.name ? owners[0].name : 'Unknown Owner';
                      })()}
                    </Typography>
                    {(invoice.client?.email || invoice.horse.ownerEmail || (() => {
                      const owners = Array.isArray(invoice.horse.owners) ? invoice.horse.owners : [];
                      return owners[0]?.email;
                    })()) && (
                      <Typography variant="body2">
                        {invoice.client?.email || invoice.horse.ownerEmail || invoice.horse.owners?.[0]?.email}
                      </Typography>
                    )}
                    {(invoice.client?.phone || invoice.horse.ownerPhone || (() => {
                      const owners = Array.isArray(invoice.horse.owners) ? invoice.horse.owners : [];
                      return owners[0]?.phone;
                    })()) && (
                      <Typography variant="body2">
                        {invoice.client?.phone || invoice.horse.ownerPhone || invoice.horse.owners?.[0]?.phone}
                      </Typography>
                    )}
                    {invoice.horse.ownerAddress && (
                      <Typography variant="body2">{invoice.horse.ownerAddress}</Typography>
                    )}
                  </>
                )}
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  PATIENT INFORMATION:
                </Typography>
                <Typography variant="body1">
                  <strong>Horse:</strong> {invoice.horse.name}
                </Typography>
                {(invoice.serviceDate || invoice.appointments?.[0]?.date) && (
                  <Typography variant="body2">
                    {/* TWO KINDS in one expression, so the `||` had to be split. Invoice.serviceDate
                        is a CALENDAR DATE (client-supplied; the create form posts it anchored at
                        noon UTC, barn operations at UTC midnight — Billing/index.tsx:1125 already
                        reads it back with parseISO(...substring(0,10))). Appointment.date is an
                        INSTANT: one DateTime carrying the start time, with `duration` in minutes
                        beside it — the appointment-time example named in the landmines rule. Each
                        branch keeps the spelling its own kind requires: the date PART for the
                        calendar day, the CLINIC's zone for the instant (DATE-KINDS §1.3). */}
                    <strong>Service Date:</strong>{' '}
                    <Box component="span" data-testid="invoice-service-date">
                      {invoice.serviceDate
                        ? formatCalendarDate(invoice.serviceDate)
                        : formatInstant(invoice.appointments![0].date, 'MMMM dd, yyyy')}
                    </Box>
                  </Typography>
                )}
                {invoice.appointments?.[0] && (
                  <Typography variant="body2">
                    <strong>Service Type:</strong> {invoice.appointments[0].type}
                  </Typography>
                )}
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            {/* Line Items */}
            <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e0e0e0' }}>
              <Table className="line-items">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell><strong>Description</strong></TableCell>
                    <TableCell align="center"><strong>Qty</strong></TableCell>
                    <TableCell align="right"><strong>Unit Price</strong></TableCell>
                    <TableCell align="right"><strong>Total</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dayGroups
                    ? dayGroups.map((group) => (
                        <React.Fragment key={group.key}>
                          <TableRow sx={{ backgroundColor: '#fafafa' }}>
                            <TableCell colSpan={4} sx={{ py: 1 }}>
                              <Typography variant="subtitle2" fontWeight="bold">
                                {group.label}
                              </Typography>
                            </TableCell>
                          </TableRow>
                          {group.items.map((item, index) => renderLineItemRow(item, `${group.key}-${index}`))}
                          <TableRow>
                            <TableCell colSpan={3} align="right">
                              <Typography variant="body2" color="text.secondary">
                                {group.subtotalLabel}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" fontWeight="bold">
                                {formatDollars(group.subtotal)}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        </React.Fragment>
                      ))
                    : lineItems.map((item, index) => renderLineItemRow(item, index))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Totals */}
            {(() => {
              const isSplit = invoice.splitGroupTotal != null && invoice.splitGroupTotal > 0;

              // Line items are always full-price. subtotal = full service subtotal.
              // For split invoices, recompute full tax from rate; for normal invoices use stored tax.
              const lineItemsArr: BillingLineItem[] = Array.isArray(invoice.lineItems) ? invoice.lineItems : [];
              // BILL-DISC: the invoice-level discount reduces the subtotal (and
              // the taxable base) BEFORE tax — stored amount is already
              // post-discount, so total = subtotal − discount + tax.
              const invoiceDiscountAmount = Number(invoice.discountAmount || 0);
              const fullTax = isSplit
                ? calculateTax(lineItemsArr, taxRate, clinicInfo?.taxMode || 'ALL', invoiceDiscountAmount)
                : taxAmount;
              const fullTotal = Math.round((subtotal - invoiceDiscountAmount + fullTax) * 100) / 100;
              // Owner's actual amount due (the split values stored on this invoice)
              const ownerTotal = Number(invoice.amount || 0) + Number(invoice.taxAmount || 0);

              return (
                <Box className="totals" mt={3}>
                  <Grid container justifyContent="flex-end">
                    <Grid item xs={5}>
                      <Box display="flex" justifyContent="space-between" mb={1}>
                        <Typography>Subtotal:</Typography>
                        <Typography fontWeight="medium" data-testid="invoice-subtotal">${subtotal.toFixed(2)}</Typography>
                      </Box>
                      {invoiceDiscountAmount > 0 && (
                        <Box display="flex" justifyContent="space-between" mb={1} alignItems="center">
                          <Box display="flex" alignItems="center" gap={0.5} sx={{ minWidth: 0 }}>
                            <Typography noWrap>
                              Discount
                              {invoice.discountType === 'percentage' && invoice.discountValue
                                ? ` (${invoice.discountValue}%)`
                                : ''}
                              :
                            </Typography>
                            {invoice.discountReason && (
                              <Chip
                                label={invoice.discountReason}
                                size="small"
                                variant="outlined"
                                color="primary"
                                sx={{ height: 18, fontSize: '0.65rem', maxWidth: 140 }}
                              />
                            )}
                          </Box>
                          <Typography color="primary" data-testid="invoice-discount">−${invoiceDiscountAmount.toFixed(2)}</Typography>
                        </Box>
                      )}
                      {fullTax > 0 && (
                        <Box display="flex" justifyContent="space-between" mb={1}>
                          <Typography>Tax{taxRate > 0 ? ` (${taxRate}%)` : ''}:</Typography>
                          <Typography data-testid="invoice-tax">${fullTax.toFixed(2)}</Typography>
                        </Box>
                      )}
                      <Divider sx={{ my: 1 }} />
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="h6" fontWeight="bold">Total:</Typography>
                        <Typography variant="h6" fontWeight="bold" data-testid="invoice-total">
                          ${fullTotal.toFixed(2)}
                        </Typography>
                      </Box>
                      {isSplit && invoice.splitPercentage != null && (
                        <Box display="flex" justifyContent="space-between" mt={1}>
                          <Typography fontWeight="bold">
                            Your Share ({invoice.splitPercentage}%):
                          </Typography>
                          <Typography fontWeight="bold" color="primary" data-testid="invoice-owner-share">
                            ${ownerTotal.toFixed(2)}
                          </Typography>
                        </Box>
                      )}
                    </Grid>
                  </Grid>
                </Box>
              );
            })()}

            {invoice.status === 'cancelled' && (
              <Box mt={2} p={1.5} sx={{ backgroundColor: '#fdecea', border: '1px solid #f5c6cb', borderRadius: 1, textAlign: 'center' }}>
                <Typography fontWeight="bold" color="error">VOIDED</Typography>
              </Box>
            )}

            {/* Payment History */}
            {balanceData && (balanceData.payments.length > 0 || (balanceData.voidedPayments?.length ?? 0) > 0 || (balanceData.refundedPayments?.length ?? 0) > 0 || (balanceData.disputedPayments?.length ?? 0) > 0) && (
              <Box mt={3}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  PAYMENTS RECEIVED
                </Typography>
                <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e0e0e0' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                        <TableCell><strong>Date</strong></TableCell>
                        <TableCell><strong>Method</strong></TableCell>
                        <TableCell align="right"><strong>Amount</strong></TableCell>
                        <TableCell><strong>Reference</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {[...balanceData.payments, ...(balanceData.voidedPayments ?? []), ...(balanceData.refundedPayments ?? []), ...(balanceData.disputedPayments ?? [])]
                        .sort((a, b) => new Date(a.paymentDate).getTime() - new Date(b.paymentDate).getTime())
                        .map((payment) => {
                        const isVoided = payment.status === 'voided';
                        const isRefunded = payment.status === 'refunded';
                        // A disputed payment is not counted while the card's bank holds the money.
                        const isDisputed = payment.status === 'disputed';
                        const struck = isVoided || isRefunded || isDisputed;
                        const refundPending = !!payment.stripeRefundId && payment.status === 'succeeded' && !payment.refundFailedAt;
                        // Manual payments void; Stripe payments refund (real
                        // money moves back, PIN-approved). A failed refund
                        // clears stripeRefundId server-side, so Refund reappears.
                        // Both are online-only money mutations — gated offline.
                        const canVoid = can.refund && payment.status === 'succeeded' && !payment.stripePaymentIntentId && !isOffline;
                        const canRefund =
                          can.refund && payment.status === 'succeeded' && !!payment.stripePaymentIntentId && !payment.stripeRefundId && !isOffline;
                        return (
                        <TableRow key={payment.id} sx={struck ? { opacity: 0.6 } : undefined}>
                          <TableCell data-testid="payment-date">
                            {/* MIXED PER ROW, and resolved per row — this is the site the
                                clinic reported ("a payment recorded for Sep 3 shows Sep 02").
                                Payment.paymentDate is a picked DAY when staff filled Date
                                Received and a real MOMENT when they did not (a card charge,
                                a statement settlement, or the `new Date()` fallback), so
                                neither helper is right for the whole column:
                                `format(new Date(x))` — what was here — read the day in the
                                BROWSER's zone and rolled every picked day back one day for
                                every US clinic, while a blanket date-part parse would move a
                                9 PM Eastern card payment into the next day's revenue.
                                `formatPaymentDay` splits them on the stored spelling; the
                                backend writes and reads the same rule
                                (backend/src/common/payment-date.ts). DATE-KINDS §1.2 W6. */}
                            {formatPaymentDay(payment.paymentDate, clinicTz, 'MMM dd, yyyy')}
                          </TableCell>
                          <TableCell>
                            {PAYMENT_TYPE_LABELS[payment.paymentType] || payment.paymentType}
                            {payment.payerName && (
                              <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                                · paid by {payment.payerName}
                              </Typography>
                            )}
                            {payment.cardBrand && payment.cardLast4 && (
                              <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                                ({payment.cardBrand} ····{payment.cardLast4})
                              </Typography>
                            )}
                            {payment.status === 'processing' && (
                              <Chip label="Processing" size="small" color="info" sx={{ ml: 1 }} />
                            )}
                            {isVoided && (
                              <Chip label="VOIDED" size="small" color="error" sx={{ ml: 1 }} />
                            )}
                            {refundPending && (
                              <Chip label="Refund processing" size="small" color="warning" sx={{ ml: 1 }} />
                            )}
                            {payment.refundFailedAt && (
                              <Chip label="Refund failed" size="small" color="error" sx={{ ml: 1 }} />
                            )}
                            {isRefunded && (
                              <Chip label="REFUNDED" size="small" color="warning" sx={{ ml: 1 }} />
                            )}
                            {isDisputed && (
                              <Chip
                                label={payment.disputeLost ? 'DISPUTE LOST' : 'DISPUTED'}
                                size="small"
                                color={payment.disputeLost ? 'error' : 'warning'}
                                sx={{ ml: 1 }}
                              />
                            )}
                            {!!payment.partiallyRefundedCents && !struck && (
                              <Chip label="Partly refunded" size="small" color="warning" sx={{ ml: 1 }} />
                            )}
                          </TableCell>
                          <TableCell align="right">
                            <span style={struck ? { textDecoration: 'line-through' } : undefined}>
                              ${payment.amount.toFixed(2)}
                            </span>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                              <Typography variant="body2" color="text.secondary">
                                {isVoided
                                  ? `Voided${payment.voidReason ? ` — ${payment.voidReason}` : ''}`
                                  : isRefunded
                                  ? `Refunded${payment.refundReason ? ` — ${payment.refundReason}` : ''}`
                                  : isDisputed
                                  ? disputeNote(payment)
                                  : payment.partiallyRefundedCents
                                  ? `${formatCents(payment.partiallyRefundedCents)} refunded in Stripe. This invoice still counts the full payment.`
                                  : payment.status === 'processing'
                                  ? 'ACH transfer in progress (1–4 business days)'
                                  : (payment.referenceNote || '—')}
                              </Typography>
                              {canVoid && (
                                <Button
                                  size="small"
                                  color="error"
                                  variant="text"
                                  className="no-print"
                                  onClick={() => setVoidTarget({ kind: 'payment', id: payment.id, label: `payment of $${payment.amount.toFixed(2)}`, method: payment.paymentMethod })}
                                  sx={{ minWidth: 0, fontSize: '0.7rem' }}
                                >
                                  Void
                                </Button>
                              )}
                              {canRefund && (
                                <Button
                                  size="small"
                                  color="error"
                                  variant="text"
                                  className="no-print"
                                  onClick={() => setRefundTarget(payment)}
                                  sx={{ minWidth: 0, fontSize: '0.7rem' }}
                                >
                                  Refund
                                </Button>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                        );
                      })}
                      <TableRow>
                        <TableCell colSpan={2} align="right">
                          <Typography fontWeight="bold">Total Paid:</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography fontWeight="bold" data-testid="invoice-total-paid">${balanceData.totalPaid.toFixed(2)}</Typography>
                        </TableCell>
                        <TableCell />
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
                <Box display="flex" justifyContent="flex-end" mt={1.5} gap={1} flexWrap="wrap">
                  {(balanceData.processingAmount ?? 0) > 0 && (
                    <Chip
                      label={`$${balanceData.processingAmount!.toFixed(2)} Processing (ACH)`}
                      color="info"
                      sx={{ fontWeight: 'bold', fontSize: '0.9rem', py: 0.5 }}
                    />
                  )}
                  {balanceData.balanceDue > 0 ? (
                    <Chip
                      label={`Balance Due: $${balanceData.balanceDue.toFixed(2)}`}
                      color="warning"
                      data-testid="invoice-balance-due"
                      sx={{ fontWeight: 'bold', fontSize: '0.9rem', py: 0.5 }}
                    />
                  ) : (balanceData.processingAmount ?? 0) > 0 ? (
                    <Chip
                      label="Fully Covered (pending settlement)"
                      color="info"
                      variant="outlined"
                      sx={{ fontWeight: 'bold', fontSize: '0.9rem', py: 0.5 }}
                    />
                  ) : (
                    <Chip
                      label="Paid in Full"
                      color="success"
                      sx={{ fontWeight: 'bold', fontSize: '0.9rem', py: 0.5 }}
                    />
                  )}
                </Box>
              </Box>
            )}

            {/* Notes (client-facing), as the PDF and email print them. */}
            {notes && (
              <Box mt={3} p={2} sx={{ backgroundColor: '#fafafa', border: '1px solid #e0e0e0', borderRadius: 1 }}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  Notes:
                </Typography>
                <Typography data-testid="invoice-notes" variant="body2" color="textSecondary" sx={{ whiteSpace: 'pre-wrap' }}>
                  {notes}
                </Typography>
              </Box>
            )}

            {/* Internal (accountant-only) note — excluded from print/email */}
            {invoice.internalNote && (
              <Box className="no-print" mt={2} p={2} sx={{ backgroundColor: '#fff8e1', border: '1px dashed #f0c36d', borderRadius: 1 }}>
                <Typography variant="caption" fontWeight="bold" color="warning.main" sx={{ display: 'block', mb: 0.5 }}>
                  INTERNAL NOTE — not shown to client
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ whiteSpace: 'pre-wrap' }}>
                  {invoice.internalNote}
                </Typography>
              </Box>
            )}

            {/* Footer */}
            <Box className="footer" mt={5} pt={3} borderTop="1px solid #e0e0e0">
              <Typography variant="body2" color="textSecondary" align="center">
                Thank you for choosing {clinicInfo?.name || 'our clinic'}
              </Typography>
              <Typography variant="caption" color="textSecondary" align="center" display="block">
                For questions about this invoice, please contact us at {clinicInfo?.email || 'our office'}
              </Typography>
            </Box>
          </Box>
        </div>
      </DialogContent>
      
      <Box className="no-print" sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
        {/* Primary action row */}
        {onRecordPayment && invoice.status !== 'paid' && invoice.status !== 'cancelled' && (
          <Box sx={{ px: 2, pt: 2, pb: 1 }}>
            <Button
              fullWidth
              startIcon={<PaymentIcon />}
              variant="contained"
              color="primary"
              onClick={onRecordPayment}
              size="large"
            >
              Record Payment
            </Button>
          </Box>
        )}

        {/* Document actions row — server round trips (email send, rendered
            PDF), so all three are disabled offline; the invoice view above
            still works offline. Voiding is a money mutation the op queue
            deliberately excludes — online-only as well. */}
        <Tooltip
          title={isOffline ? 'PDF, print and email need a connection — the invoice view above works offline' : ''}
        >
          <Box sx={{ display: 'flex', gap: 1, px: 2, pb: 2, pt: 1, justifyContent: 'center' }}>
            <Button
              startIcon={sendingEmail ? <CircularProgress size={16} /> : <EmailIcon />}
              variant="text"
              size="small"
              onClick={handleEmailInvoice}
              disabled={sendingEmail || generatingPDF || preparingPrint || isOffline}
              sx={{ flex: 1, minWidth: 0, fontSize: '0.8rem' }}
            >
              {sendingEmail ? 'Sending...' : emailSent ? 'Sent!' : invoice.status === 'paid' ? 'Receipt' : 'Email'}
            </Button>
            <Button
              startIcon={generatingPDF ? <CircularProgress size={16} /> : <DownloadIcon />}
              variant="text"
              size="small"
              onClick={handleDownloadPDF}
              disabled={generatingPDF || preparingPrint || isOffline}
              sx={{ flex: 1, minWidth: 0, fontSize: '0.8rem' }}
            >
              {generatingPDF ? 'Generating...' : 'PDF'}
            </Button>
            <Button
              startIcon={preparingPrint ? <CircularProgress size={16} /> : <PrintIcon />}
              variant="text"
              size="small"
              onClick={handlePrint}
              disabled={preparingPrint || generatingPDF || isOffline}
              sx={{ flex: 1, minWidth: 0, fontSize: '0.8rem' }}
            >
              {preparingPrint ? 'Preparing...' : 'Print'}
            </Button>
            {invoice.status !== 'cancelled' && (() => {
              // BILL-E void eligibility: money moved on THIS invoice, or on a
              // co-owner's sibling in the split group (voidBlockedReason
              // 'co_owner_paid' from findOne), kills the void — the action
              // swaps to "Issue Credit" (visible correction, never an erase).
              const selfPaid =
                (balanceData?.totalPaid ?? 0) > 0 || (balanceData?.processingAmount ?? 0) > 0;
              // Post-void refetch (freshVoidState) wins over the parent's
              // stale findOne snapshot so the Void button appears immediately.
              const voidEligible = freshVoidState ? freshVoidState.voidEligible : invoice.voidEligible;
              const voidBlockedReason = freshVoidState
                ? freshVoidState.voidBlockedReason
                : invoice.voidBlockedReason;
              const coOwnerPaid = voidBlockedReason === 'co_owner_paid';
              const voidBlocked = selfPaid || coOwnerPaid || voidEligible === false;
              if (voidBlocked) {
                return (
                  <Tooltip
                    title={
                      !invoice.clientId
                        ? 'Credits need a client account — this invoice is billed to a one-time payer'
                        : coOwnerPaid && !selfPaid
                        ? 'A co-owner already paid part of this split group, so it can no longer be voided. Issue a credit to offset what this client owes.'
                        : 'This invoice has payments, so it can no longer be voided. Issue a credit to correct it visibly.'
                    }
                  >
                    <span style={{ flex: 1, minWidth: 0, display: 'flex' }}>
                      <Button
                        variant="text"
                        size="small"
                        color="warning"
                        disabled={isOffline || !invoice.clientId}
                        onClick={() => setIssueCreditOpen(true)}
                        sx={{ flex: 1, minWidth: 0, fontSize: '0.8rem' }}
                      >
                        Issue Credit
                      </Button>
                    </span>
                  </Tooltip>
                );
              }
              // ⚠ BELOW the `voidBlocked` branch on purpose. Voiding an invoice is
              // `billing.controller.voidInvoice` → `manage:refunds`; the Issue Credit fallback
              // above it is `POST /credits` → `manage:discounts`, a different slug that plenty of
              // front-desk people hold on its own. Gating the whole block would have taken the
              // visible-correction path away from exactly the person it exists for.
              if (!can.refund) return null;
              return (
                <Tooltip title={isOffline ? 'Voiding needs a connection' : ''}>
                  <span style={{ flex: 1, minWidth: 0, display: 'flex' }}>
                    <Button
                      variant="text"
                      size="small"
                      color="error"
                      disabled={isOffline}
                      onClick={() => setVoidTarget({ kind: 'invoice', id: invoice.id, label: `invoice #${invoice.invoiceNumber}` })}
                      sx={{ flex: 1, minWidth: 0, fontSize: '0.8rem' }}
                    >
                      Void
                    </Button>
                  </span>
                </Tooltip>
              );
            })()}
          </Box>
        </Tooltip>
      </Box>

      {/* One maker-checker dialog for all money reversals: Stripe refunds and
          voids of payments/invoices (any tender) — admin/owner PIN required.
          Mount-gated so the content can't swap to the other mode's copy
          during the close transition. */}
      {(refundTarget || voidTarget) && (
        <RefundPaymentDialog
          open
          payment={refundTarget}
          voidTarget={voidTarget}
          onClose={() => {
            setRefundTarget(null);
            setVoidTarget(null);
          }}
          onSuccess={() => {
            // Voiding a credit application writes a void_reversal ledger entry
            // that restores the client's balance — refresh the Available
            // Credit card or it shows the stale pre-void figure.
            if (voidTarget?.method === 'credit' && invoice.clientId) {
              queryClient.invalidateQueries(['client-credits', invoice.clientId]);
            }
            if (voidTarget?.kind === 'invoice') {
              onClose(); // the invoice is gone from the open view — close it
            } else {
              setRefreshKey((k) => k + 1); // refetch balance so the void/refund shows
              // Re-read void eligibility so the invoice Void button appears
              // right away instead of after close/reopen. Failure keeps the
              // stale props — same behavior as before, offline-safe.
              const forInvoiceId = invoice.id;
              api
                .get(`/billing/${forInvoiceId}`)
                .then((r) => {
                  if (invoiceIdRef.current !== forInvoiceId) return;
                  setFreshVoidState({
                    voidEligible: r.data?.voidEligible,
                    voidBlockedReason: r.data?.voidBlockedReason ?? null,
                  });
                })
                .catch(() => {});
            }
          }}
        />
      )}

      {/* BILL-E: credit-in-lieu-of-void — the original invoice (discounts
          included) stays exactly as billed; the credit offsets what's owed. */}
      {invoice.clientId && (
        <IssueCreditDialog
          open={issueCreditOpen}
          onClose={() => setIssueCreditOpen(false)}
          clientId={invoice.clientId}
          clientName={invoice.client?.name}
          defaultAmount={balanceData?.balanceDue}
          defaultSource="void_in_lieu"
          linkedInvoiceId={invoice.id}
          linkedInvoiceNumber={invoice.invoiceNumber}
        />
      )}
    </Dialog>
  );
};

export default InvoiceDetail;
